import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { usePageMeta } from '../hooks/usePageMeta';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Wrench,
  Palette, Globe, Printer, Camera, FileText, CreditCard,
  Zap, Clock, Star
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

// Quick Fix Card Component
const QuickFixCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  
  const iconMap = {
    'business-card': CreditCard,
    'google': Globe,
    'social-cover': Camera,
    'flyer': FileText,
    'yard-sign': Printer,
    'website': Globe,
    'logo': Palette,
    'social-templates': Camera
  };
  
  const Icon = iconMap[service.iconKey] || Sparkles;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl hover:border-[#1E3A8A]/20 transition-all duration-300 group relative overflow-hidden"
    >
      {/* Hover gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      />
      
      {/* Popular badge */}
      {service.popular && (
        <div className="absolute top-4 right-4 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
          <Star className="w-3 h-3" /> Popular
        </div>
      )}
      
      <div className="relative z-10">
        {/* Icon */}
        <motion.div
          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
          transition={{ duration: 0.4 }}
          className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
          style={{ background: `linear-gradient(135deg, ${service.color}20 0%, ${service.color}10 100%)` }}
        >
          <Icon className="w-7 h-7" style={{ color: service.color }} />
        </motion.div>
        
        {/* Title & Price */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#1E3A8A] transition-colors pr-2">
            {service.title}
          </h3>
          <span className="text-lg font-bold text-[#1E3A8A] whitespace-nowrap">{service.price}</span>
        </div>
        
        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.description}</p>
        
        {/* Turnaround */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
          <Clock className="w-3 h-3" />
          <span>{service.turnaround}</span>
        </div>
        
        {/* Features */}
        <ul className="space-y-2 mb-5">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
        
        {/* CTA */}
        <Link to={service.link}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-xl">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </Link>
      </div>
    </motion.div>
  );
};

const MiniServicesPage = () => {
  usePageMeta({
    title: 'Quick Fixes | BM-Assist.co',
    description: 'Individual marketing services for contractors and home service businesses. Business cards, Google setup, flyers, and more - starting at just $39.',
    ogTitle: 'Quick Fixes | BM-Assist.co',
    ogDescription: 'Need just one thing? Get professional marketing materials without buying a full package.'
  });

  const services = [
    {
      iconKey: 'business-card',
      title: 'Business Card Design',
      price: 'from $49',
      description: 'Professional business card designed for your trade. Clean, modern, and ready for print.',
      turnaround: '24-48 hours',
      features: [
        'Custom design to match your brand',
        'Print-ready files (PDF, PNG)',
        'Up to 2 revision rounds'
      ],
      color: '#1E3A8A',
      link: '/mini-services/request?service=Business%20Card%20Design',
      popular: true
    },
    {
      iconKey: 'google',
      title: 'Google Business Setup',
      price: 'from $99',
      description: 'Get your Google Business Profile created, verified, and optimized for local searches.',
      turnaround: '2-3 days',
      features: [
        'Profile creation & verification',
        'Business info optimization',
        'First photos uploaded'
      ],
      color: '#10B981',
      link: '/mini-services/request?service=Google%20Business%20Profile%20Setup',
      popular: true
    },
    {
      iconKey: 'social-cover',
      title: 'Social Media Cover',
      price: 'from $39',
      description: 'Eye-catching cover image for Facebook or Instagram, sized and formatted perfectly.',
      turnaround: '24 hours',
      features: [
        'Custom branded design',
        'Sized per platform specs',
        '1 revision round'
      ],
      color: '#8B5CF6',
      link: '/mini-services/request?service=Social%20Media%20Cover%20Graphic'
    },
    {
      iconKey: 'flyer',
      title: 'Flyer / Door Hanger',
      price: 'from $59',
      description: 'Print-ready flyer or door hanger to promote your services in your local area.',
      turnaround: '48 hours',
      features: [
        'Eye-catching design',
        'Print-ready files',
        '1 revision round'
      ],
      color: '#F59E0B',
      link: '/mini-services/request?service=Flyer%20or%20Door%20Hanger%20Design'
    },
    {
      iconKey: 'yard-sign',
      title: 'Yard Sign Design',
      price: 'from $69',
      description: 'Bold, readable yard sign that grabs attention and drives calls.',
      turnaround: '48 hours',
      features: [
        'High-visibility design',
        'Print-ready files',
        'Standard sizes available'
      ],
      color: '#EF4444',
      link: '/mini-services/request?service=Yard%20Sign%20Design'
    },
    {
      iconKey: 'logo',
      title: 'Simple Logo Design',
      price: 'from $149',
      description: 'Clean, professional logo or wordmark that represents your business.',
      turnaround: '3-5 days',
      features: [
        '3 initial concepts',
        'Up to 3 revision rounds',
        'All file formats included'
      ],
      color: '#EC4899',
      link: '/mini-services/request?service=Simple%20Logo%20Design'
    },
    {
      iconKey: 'website',
      title: 'One-Page Website',
      price: 'from $199',
      description: 'Clean, mobile-friendly one-page site with your info and contact form.',
      turnaround: '5-7 days',
      features: [
        'Mobile responsive design',
        'Contact form included',
        '1 year hosting included'
      ],
      color: '#06B6D4',
      link: '/mini-services/request?service=Simple%20One-Page%20Website'
    },
    {
      iconKey: 'social-templates',
      title: 'Social Post Templates',
      price: 'from $79',
      description: '5 branded social media post templates you can edit and reuse.',
      turnaround: '3-4 days',
      features: [
        '5 unique templates',
        'Canva-editable files',
        'Sized for Instagram/Facebook'
      ],
      color: '#14B8A6',
      link: '/mini-services/request?service=Social%20Post%20Templates'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated background elements */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-amber-200 rounded-full filter blur-3xl opacity-30"
          animate={{ x: [0, 20, 0], y: [0, -15, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 right-10 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-30"
          animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-200 rounded-full filter blur-3xl opacity-20"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Wrench className="w-4 h-4" />
              Quick Fixes
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span className="text-gray-900">Just need </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                one thing?
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed"
            >
              Individual services for when you don't need the full package. 
              Get exactly what you need, nothing more.
            </motion.p>

            {/* Quick stats */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-6 text-sm"
            >
              {[
                { icon: Zap, text: "24-48hr turnaround", color: "#F59E0B" },
                { icon: Star, text: "Starting at $39", color: "#10B981" },
                { icon: CheckCircle2, text: "No bundles required", color: "#1E3A8A" }
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-md border border-gray-100"
                >
                  <item.icon className="w-4 h-4" style={{ color: item.color }} />
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service, index) => (
              <QuickFixCard key={service.title} service={service} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bundle CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Need more than one thing?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Check out our Launch Bundles for better value when you need the full package.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/bundles">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white text-lg px-8 py-6 rounded-full shadow-xl">
                  View Launch Bundles
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
            <Link to="/request">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white text-lg px-8 py-6 rounded-full">
                  Custom Request
                </Button>
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/bundles" className="hover:text-white transition-colors">Launch Bundles</Link>
              <Link to="/team" className="hover:text-white transition-colors">Our Team</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MiniServicesPage;
